import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loading from '../common/Loading';
import {
  fetchCollectiveInvite,
  submitCollectiveText,
  submitCollectivePhoto,
  finishCollectiveContribution,
  saveCollectiveName
} from '../../services/compositionApi';
import '../../styles/luxe-theme.css';
import UploadProgress from './UploadProgress';
import MesEnvois from './MesEnvois';
import './CollectiveGuest.css';

// Page de l'invite (/collectif/:token) — refonte du collectif, phase 4 (2026-10-24, maquette docs/bookipix-collectif.html).
// Mobile d'abord, trois ecrans : 1. le lien recu (qui invite, message, date limite, prenom, UN bouton « Ajouter mes photos »),
// 2. le choix des photos (cases a cocher, compteur, mot facultatif, bouton qui reprend le nombre), 3. merci (recapitulatif,
// « ajouter d'autres photos » jusqu'a la date limite, lien discret « Creer mon livre photo »).
// Aucun compte. L'invite ne voit jamais les envois des autres. Envoi interrompu : les photos non parties restent choisies et
// « Reprendre l'envoi » ne renvoie qu'elles. Photo tres lourde : reduite dans le navigateur avant l'envoi.
// « Mes envois » (2026-10-29) : l'invite voit ses envois et retire ceux qui ne sont pas encore places dans le livre (MesEnvois.js).

const LIMITE_SERVEUR = 20 * 1024 * 1024; // middleware/upload.js
const SEUIL_REDUCTION = 15 * 1024 * 1024;

// Une photo tres lourde (au-dela de 15 Mo) est reduite a 4000 px avant l'envoi : le serveur refuse au-dela de 20 Mo, et 4000 px
// suffisent largement a l'impression. Les formats que le navigateur ne sait pas redessiner (HEIC…) partent tels quels.
async function preparerPhoto(file) {
  if (file.size <= SEUIL_REDUCTION || !/^image\/(jpeg|png|webp)$/.test(file.type) || typeof createImageBitmap !== 'function') return file;
  try {
    const image = await createImageBitmap(file);
    const echelle = Math.min(1, 4000 / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.width * echelle);
    canvas.height = Math.round(image.height * echelle);
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    return blob ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }) : file;
  } catch (_e) {
    return file;
  }
}

let compteur = 0;

const CollectiveParticipateLuxe = () => {
  const { t, i18n } = useTranslation('collective');
  const { token } = useParams();
  const choixRef = useRef(null);
  const prenomRef = useRef(null);

  const [chargement, setChargement] = useState(true);
  const [lienInvalide, setLienInvalide] = useState(false);
  const [info, setInfo] = useState(null);
  const [etape, setEtape] = useState('accueil'); // 'accueil' | 'choix' | 'merci'
  const [prenom, setPrenom] = useState('');
  const [prenomEnregistre, setPrenomEnregistre] = useState('');
  const [selection, setSelection] = useState([]); // [{ id, file, url, choisie, etat: 'attente'|'ok'|'erreur', erreur }]
  const [mot, setMot] = useState('');
  const [motEnvoye, setMotEnvoye] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [progression, setProgression] = useState({ fait: 0, total: 0, fraction: 0 });
  const [erreur, setErreur] = useState('');
  const [recap, setRecap] = useState({ photos: 0, mot: false });

  useEffect(() => {
    if (!token) { setLienInvalide(true); setChargement(false); return; }
    fetchCollectiveInvite(token)
      .then((r) => {
        setInfo(r);
        if (r.participantName) { setPrenom(r.participantName); setPrenomEnregistre(r.participantName); }
      })
      .catch(() => setLienInvalide(true))
      .finally(() => setChargement(false));
  }, [token]);

  // Les apercus locaux sont liberes quand la page se ferme (jamais pendant : ils sont encore affiches).
  const apercusRef = useRef([]);
  useEffect(() => () => apercusRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const assurerPrenom = async () => {
    const valeur = prenom.trim();
    if (valeur.length < 2) { setErreur(t('participate.nameRequired')); prenomRef.current?.focus(); return false; }
    if (valeur === prenomEnregistre) return true;
    try { await saveCollectiveName(token, valeur); setPrenomEnregistre(valeur); return true; } catch (err) { setErreur(err.message || t('participate.sendFailed')); return false; }
  };

  const ajouterFichiers = (event) => {
    const fichiers = Array.from(event.target.files || []);
    event.target.value = '';
    if (!fichiers.length) return;
    setSelection((prec) => [
      ...prec,
      ...fichiers.map((file) => {
        const url = URL.createObjectURL(file);
        apercusRef.current.push(url);
        return { id: `f${compteur += 1}`, file, url, choisie: true, etat: 'attente', erreur: '' };
      })
    ]);
    setEtape('choix');
    setErreur('');
  };

  const ouvrirChoix = async () => {
    setErreur('');
    if (!(await assurerPrenom())) return;
    choixRef.current?.click();
  };

  const basculer = (id) => setSelection((prec) => prec.map((s) => (s.id === id && s.etat !== 'ok' ? { ...s, choisie: !s.choisie } : s)));

  const aEnvoyer = selection.filter((s) => s.choisie && s.etat !== 'ok');
  const motAEnvoyer = mot.trim() && !motEnvoye;
  const libelleEnvoi = () => {
    const n = aEnvoyer.length;
    if (n && motAEnvoyer) return t('guest.sendPhotosAndWord', { count: n });
    if (n) return t('guest.sendPhotos', { count: n });
    if (motAEnvoyer) return t('guest.sendWord');
    return t('guest.chooseSomething');
  };

  const envoyer = async () => {
    if (!aEnvoyer.length && !motAEnvoyer) return;
    if (!(await assurerPrenom())) return;
    setEnvoi(true);
    setErreur('');
    const liste = aEnvoyer;
    setProgression({ fait: 0, total: liste.length, fraction: 0 });
    let reussies = 0;
    let derniereErreur = '';
    for (const element of liste) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const fichier = await preparerPhoto(element.file);
        if (fichier.size > LIMITE_SERVEUR) throw new Error(t('guest.tooHeavy'));
        // eslint-disable-next-line no-await-in-loop
        await submitCollectivePhoto(token, fichier, { onProgress: (fraction) => setProgression((p) => ({ ...p, fraction })) });
        reussies += 1;
        setSelection((prec) => prec.map((s) => (s.id === element.id ? { ...s, etat: 'ok', erreur: '' } : s)));
      } catch (err) {
        derniereErreur = err.message || t('participate.sendFailed');
        setSelection((prec) => prec.map((s) => (s.id === element.id ? { ...s, etat: 'erreur', erreur: derniereErreur } : s)));
      }
      setProgression((p) => ({ ...p, fait: p.fait + 1, fraction: 0 }));
    }
    let motOk = motEnvoye;
    if (motAEnvoyer) {
      try { await submitCollectiveText(token, mot.trim()); motOk = true; setMotEnvoye(true); } catch (err) { derniereErreur = err.message || t('participate.sendFailed'); }
    }
    setEnvoi(false);
    const echecs = liste.length - reussies;
    if (echecs > 0 || (mot.trim() && !motOk)) {
      setErreur(echecs > 0 ? t('guest.someFailed', { count: echecs, reason: derniereErreur }) : derniereErreur);
      setRecap((r) => ({ photos: r.photos + reussies, mot: r.mot || motOk }));
      return;
    }
    // Tout est parti : « J'ai termine » (le createur est prevenu tout de suite), puis merci.
    finishCollectiveContribution(token).catch(() => { /* le createur sera prevenu par le releve periodique */ });
    setRecap((r) => ({ photos: r.photos + reussies, mot: r.mot || motOk }));
    relireMesEnvois();
    setEtape('merci');
  };

  // Apres un envoi : relire « Mes envois » (le serveur dit ce qui est deja place). Sans effet visible si la lecture echoue.
  const relireMesEnvois = () => fetchCollectiveInvite(token).then((r) => setInfo((i) => ({ ...i, myItems: r.myItems, myCounts: r.myCounts }))).catch(() => {});
  const apresRetrait = (id) => setInfo((i) => {
    const myItems = (i.myItems || []).filter((x) => x.id !== id);
    return { ...i, myItems, myCounts: { photos: myItems.filter((x) => x.kind === 'photo').length, souvenirs: myItems.filter((x) => x.kind === 'texte').length } };
  });

  const recommencer = () => {
    setSelection([]);
    setMot('');
    setMotEnvoye(false);
    setRecap({ photos: 0, mot: false });
    setErreur('');
    setEtape('accueil');
  };

  if (chargement) return <Loading message={t('participate.loading')} />;

  if (lienInvalide || !info) {
    return (
      <main className="cg-page">
        <section className="cg-card cg-center cg-short">
          <h1>{t('guest.invalidTitle')}</h1>
          <p className="cg-muted">{t('guest.invalidBody')}</p>
        </section>
      </main>
    );
  }

  const organisateur = info.organizerName || t('guest.someone');
  const titre = info.eventTitle || info.bookTitle;
  const date = info.deadline ? new Date(`${info.deadline}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) : '';

  // Livre paye et verrouille (phase 5) : le lien recu ne sert plus, on le dit clairement.
  if (info.isFinished) {
    return (
      <main className="cg-page">
        <section className="cg-card cg-center cg-short">
          <div className="cg-brand">Book<i>i</i>pix</div>
          <h1>{t('guest.finishedTitle')}</h1>
          <p className="cg-muted">{t('guest.finishedBody', { name: organisateur })}</p>
        </section>
      </main>
    );
  }

  if (info.isClosed) {
    return (
      <main className="cg-page">
        <section className="cg-card cg-center cg-short">
          <div className="cg-brand">Book<i>i</i>pix</div>
          <h1>{t('guest.closedTitle')}</h1>
          <p className="cg-muted">{t('guest.closedBody', { name: organisateur, date })}</p>
        </section>
      </main>
    );
  }

  const inputFichiers = (
    <input ref={choixRef} type="file" accept="image/*" multiple hidden onChange={ajouterFichiers} />
  );

  if (etape === 'merci') {
    return (
      <main className="cg-page">
        <section className="cg-card cg-center" aria-live="polite">
          <div className="cg-brand">Book<i>i</i>pix</div>
          <div className="cg-ok" aria-hidden="true">✓</div>
          <h1>{t('guest.thanksTitle', { name: prenomEnregistre || prenom })}</h1>
          <p className="cg-muted">
            {t('guest.thanksBody', {
              what: [recap.photos ? t('guest.nPhotos', { count: recap.photos }) : null, recap.mot ? t('guest.aWord') : null].filter(Boolean).join(t('guest.and')) || t('guest.yourMemories'),
              name: organisateur,
              date
            })}
          </p>
          <div className="cg-sp" />
          <button type="button" className="cg-btn cg-ghost" onClick={recommencer}>{t('guest.addMore')}</button>
          <MesEnvois token={token} items={info.myItems} onRetire={apresRetrait} />
          <div className="cg-own">
            <p className="cg-muted">{t('guest.ownBook')}</p>
            <Link to="/" className="cg-link">{t('guest.createMine')}</Link>
          </div>
        </section>
      </main>
    );
  }

  if (etape === 'choix') {
    return (
      <main className="cg-page">
        <section className="cg-card">
          {inputFichiers}
          <button type="button" className="cg-link cg-back" onClick={() => setEtape('accueil')} disabled={envoi}>← {t('guest.back')}</button>
          <h1>{t('guest.pickTitle')}</h1>
          <p className="cg-muted">{t('guest.pickHint', { date })}</p>
          {selection.length > 0 && (
            <div className="cg-pick" role="group" aria-label={t('guest.pickTitle')}>
              {selection.map((s, index) => (
                <button
                  key={s.id}
                  type="button"
                  className={`cg-thumb is-${s.etat}`}
                  aria-pressed={s.choisie}
                  aria-label={t('guest.photoN', { n: index + 1 }) + (s.etat === 'ok' ? ` — ${t('guest.sent')}` : '') + (s.etat === 'erreur' ? ` — ${s.erreur}` : '')}
                  onClick={() => basculer(s.id)}
                  disabled={envoi || s.etat === 'ok'}
                  title={s.etat === 'erreur' ? s.erreur : undefined}
                >
                  <img src={s.url} alt="" />
                  {s.etat === 'ok' && <span className="cg-badge-ok">{t('guest.sent')}</span>}
                  {s.etat === 'erreur' && <span className="cg-badge-err">!</span>}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="cg-btn cg-ghost" onClick={() => choixRef.current?.click()} disabled={envoi}>
            + {selection.length ? t('guest.addOtherPhotos') : t('guest.choosePhotos')}
          </button>
          <label className="cg-field">
            <span>{t('guest.wordLabel')} <em>{t('guest.optional')}</em></span>
            <textarea rows={3} value={mot} onChange={(e) => setMot(e.target.value)} placeholder={t('guest.wordPlaceholder', { name: organisateur })} disabled={envoi || motEnvoye} maxLength={2000} />
          </label>
          {envoi && <UploadProgress fait={progression.fait} total={progression.total} fraction={progression.fraction} />}
          {erreur && <p className="cg-err" role="alert">{erreur}</p>}
          <div className="cg-sp" />
          <button type="button" className="cg-btn" onClick={envoyer} disabled={envoi || (!aEnvoyer.length && !motAEnvoyer)}>
            {envoi ? t('guest.sending') : (erreur && aEnvoyer.length ? t('guest.resume', { count: aEnvoyer.length }) : libelleEnvoi())}
          </button>
        </section>
      </main>
    );
  }

  // Accueil : le lien reçu.
  const deja = info.myCounts && (info.myCounts.photos + info.myCounts.souvenirs) > 0 ? info.myCounts : null;
  return (
    <main className="cg-page">
      <section className="cg-card">
        {inputFichiers}
        <div className="cg-brand">Book<i>i</i>pix</div>
        <div className="cg-cover" aria-hidden="true"><span>{titre}</span></div>
        <h1>{t('guest.invites', { name: organisateur })}</h1>
        {info.message && <p className="cg-muted cg-quote">« {info.message} »</p>}
        {date && <p className="cg-deadline"><span aria-hidden="true">⏰</span> {t('guest.before', { date })}</p>}
        {deja && <p className="cg-already">{t('guest.already', { what: [deja.photos ? t('guest.nPhotos', { count: deja.photos }) : null, deja.souvenirs ? t('guest.nWords', { count: deja.souvenirs }) : null].filter(Boolean).join(t('guest.and')) })}</p>}
        <MesEnvois token={token} items={info.myItems} onRetire={apresRetrait} />
        <label className="cg-field" htmlFor="cg-prenom">
          <span>{t('participate.nameLabel')}</span>
          <input id="cg-prenom" ref={prenomRef} value={prenom} onChange={(e) => setPrenom(e.target.value)} maxLength={80} autoComplete="given-name" placeholder={t('participate.namePlaceholder')} />
          <small>{t('participate.nameHint')}</small>
        </label>
        {erreur && <p className="cg-err" role="alert">{erreur}</p>}
        <div className="cg-sp" />
        <button type="button" className="cg-btn" onClick={ouvrirChoix}>
          <span aria-hidden="true">📷</span> {t('guest.addPhotos')}
        </button>
        <button type="button" className="cg-link cg-center-link" onClick={async () => { setErreur(''); if (await assurerPrenom()) setEtape('choix'); }}>
          {t('guest.wordOnly')}
        </button>
        <p className="cg-fine">{t('guest.privacy', { name: organisateur })}</p>
      </section>
    </main>
  );
};

export default CollectiveParticipateLuxe;
